/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import { Box, ButtonBase, TooltipProps } from '@mui/material';
import { FC, ReactNode, SVGProps, MouseEvent, useCallback } from 'react';
import { useCustomNavigate } from '../../../hooks/use-conversation-navigate';
import { trackDemoAccountDialogViewed, trackEvent } from '../../../../infra/analytics/utils';
import { RoutePages } from '../../../constants/routes';
import { Screens } from '../../../typings/screens';
import QSTooltip from '../tooltip';
import { useSetAtom } from 'jotai';
import { demoAccountAtom } from '../../../modules/demo-account/v1/states/demo-account';
import { openPopup } from '../../../utils/popup';
import LockFilledIcon from '../../../icons/icons-revamp/lock';

const SidebarMenuItem: FC<{
  Icon: FC<
    SVGProps<SVGSVGElement> & {
      size?: number | undefined;
    }
  >;
  hasAccess?: boolean;
  path?: string;
  title: TooltipProps['title'];
  withBetaTag?: boolean;
  showExpandedView: boolean;
  onClick?: (event: MouseEvent) => void;
  style?: SerializedStyles;
  component?: () => void;
  analyticsEvent?: string;
  screenName?: RoutePages;
  hideRipple?: boolean;
  hide?: boolean;
  trailingIcon?: ReactNode;
}> = ({
  Icon,
  title,
  withBetaTag,
  path,
  hasAccess,
  onClick,
  showExpandedView = false,
  style,
  component,
  analyticsEvent,
  screenName,
  hideRipple,
  hide,
  trailingIcon,
}) => {
  const navigate = useCustomNavigate();
  const openOwnAccountModal = useSetAtom(demoAccountAtom);
  const getCurrentPage = () => {
    if (
      !path ||
      !screenName ||
      (screenName === RoutePages.AI_BOT && window.location.pathname.includes(RoutePages.SETTINGS)) ||
      (screenName === RoutePages.CX_OVERVIEW && window.location.pathname.includes(RoutePages.SETTINGS))
    )
      return false;
    if (window.location.pathname === '/' && path === '/') {
      return true;
    }
    if (path.includes(screenName) && window.location.pathname.includes(path)) {
      return true;
    }
  };

  const color = getCurrentPage() ? '#3D906D' : showExpandedView ? '#2d2d2d' : '#7a7a7a';
  const bgColor = getCurrentPage() ? '#F0F5F0' : '';
  const isLocked = typeof hasAccess !== 'undefined' && !hasAccess;

  const openCreateOwnAccountModal = useCallback(() => {
    openOwnAccountModal({ open: true });
    trackDemoAccountDialogViewed();
  }, [openOwnAccountModal]);

  const lockIcon = isLocked ? (
    <LockFilledIcon
      css={css({
        marginLeft: 'auto',
        height: '15px',
        width: '15px',
        background: 'rgb(224, 224, 224)',
        padding: '2px',
        borderRadius: '50%',
        border: '1px solid #cdcdcd',
        position: 'absolute',
        right: '-2px',
        top: '-6px',
      })}
      size={18}
    />
  ) : null;

  const titleContent = withBetaTag ? (
    <Box
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        column-gap: 12px;
      `}
    >
      <Box
        css={css`
          color: inherit;
          background-color: transparent;
        `}
      >
        {title}
      </Box>
      <Box
        css={css`
          font-size: 10px;
          font-weight: 600;
          line-height: 1.5;
          color: #ffffff;
          background-color: #adadad;
          padding: 4px 8px;
          border-radius: 6px;
        `}
      >
        BETA
      </Box>
    </Box>
  ) : (
    title
  );

  let content =
    typeof component === 'function' ? (
      component()
    ) : (
      <h2
        css={css(css`
          flex: 1 1 0;
          align-self: center;
          font-size: 14px;
          margin: 0 0 0 10px;
          color: ${color};
          font-weight: 500;
          overflow: hidden;
          white-space: nowrap;
        `)}
      >
        {titleContent}
      </h2>
    );

  if (hide) return null;

  return (
    <QSTooltip
      styles={{
        tooltip: {
          marginLeft: '6px !important',
        },
      }}
      title={!showExpandedView ? title : ''}
      arrow
      placement="right"
    >
      <ButtonBase<'div'>
        css={css(
          css`
            width: 100%;
            padding: ${typeof component === 'function' ? '0px 14px' : '8px 14px'};
            font-weight: 600;
            text-align: left;
            justify-content: flex-start;
            align-items: center;
            position: relative;
            cursor: ${!isLocked && !hideRipple ? 'pointer' : 'default'};
            background: ${bgColor};
            height: 40px;

            ${hideRipple
              ? ''
              : `
              &:hover {
                background: #ebebebde;
              }
            `}
          `,
          style
        )}
        onClick={(event) => {
          if (analyticsEvent)
            trackEvent(analyticsEvent, {
              from: Screens.NavigationFragment,
              hasAccess: !!hasAccess,
            });

          if (isLocked && !onClick) {
            openCreateOwnAccountModal();
            return;
          }

          if (path) {
            if (
              event.ctrlKey ||
              event.shiftKey ||
              event.metaKey || // apple
              (event.button && event.button === 1) // middle click, >IE9 + everyone else
            ) {
              openPopup(path, '_blank');
              return;
            } else {
              navigate({ pathname: path }, { clearExistingParams: true });
            }
          } else {
            onClick?.(event);
          }
        }}
        disableRipple={!showExpandedView || hideRipple}
        component="div"
      >
        <>
          <Box
            css={css`
              position: relative;
            `}
          >
            <Icon
              css={css`
                flex: 0 0 auto;
                transition: all 280ms ease-in-out;
                display: flex;
                justify-content: center;
                align-items: center;
                width: 28px;
              `}
              color={color}
              fill={color}
            />
            {lockIcon}
          </Box>

          {content}
          {trailingIcon && showExpandedView && (
            <Box
              css={css`
                display: flex;
                align-items: center;
                margin-left: auto;
                flex-shrink: 0;
              `}
            >
              {trailingIcon}
            </Box>
          )}
        </>
      </ButtonBase>
    </QSTooltip>
  );
};

export default SidebarMenuItem;
